import { useCallback } from 'react';
import useTheme from './useTheme';
import { getComponentTheme, getThemeTokens, resolveThemeTokens, mergeAppearances } from './utils';
/**
 * @typedef {import('../utils/props/variantProp.js').AppearanceSet} AppearanceSet
 * @typedef {import('../utils/props/tokens.js').TokensProp} TokensProp
 * @typedef {import('../utils/props/tokens.js').TokensSet} TokensSet
 */

/**
 * Returns a complete set of theme tokens for a component based on which of the
 * component's theme rules apply to the current set of theme appearances.
 *
 * When applying theme tokens as styles to a component, don't spread the theme tokens
 * object, explicitly select each property. This way, if new theme tokens are added to the
 * component's theme in a minor or patch release of the theme package, the appearance
 * of the component in sites and apps will only change when the component itself is updated.
 *
 * @example
 * const seletContainerTokens = ({ color, width, height }) => ({
 *   backgroundColor: color, width, height
 * })
 * const MyComponent = ({ tokens, variant, children }) => {
 *   const viewport = useViewport()
 *   const themeTokens = useThemeTokens('MyComponent', tokens, variant, { viewport })
 *   const style = seletContainerTokens(themeTokens)
 *   return <View style={style}>{children}</View>
 * }
 *
 * @param {string} componentName - the name as defined in the theme schema of the component whose theme is to be used
 * @param {TokensProp} [tokens] - every themed component should accept an optional `tokens` prop allowing theme tokens to be overridden
 * @param {AppearanceSet} [variants] - every themed component should accept an optional `variants` prop specifying theme variants
 * @param {AppearanceSet} [states] - optional object containing current theme appearances dictated by user action or context
 * @returns {TokensSet} - the currently-applicable resolved set of theme tokens to apply
 */
export const useThemeTokens = function (componentName) {
  let tokens = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let variants = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  let states = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  const theme = useTheme();
  const componentTheme = getComponentTheme(theme, componentName);
  const themeTokens = getThemeTokens(componentTheme, tokens, variants, states);
  return themeTokens;
};

/**
 * Returns a memoised tokens getter function that gets tokens similar to calling useThemeTokens.
 * Scenarios where `useThemeTokensCallback` should be used instead of `useThemeTokens` include:
 *
 * - Where tokens are to be obtained from state that is accessible only in scopes like callbacks
 *   and render functions, where calling useThemeTokens directly would be disallowed by React's hook rules.
 * - Passing a tokens getter down via a child component's `tokens` prop, applying rules using the
 *   child component's current state. Consider wrapping the returned tokens in `selectTokens()`.
 *
 * The function returned by `useThemeTokens` may be called with an object of state appearances to get an object
 * of tokens, or may be passed as a `tokens` prop to other themed components or `useThemeTokens`/`useThemeTokensCallback`
 * hooks.
 *
 * @example
 * // Resolving tokens inside Pressable's style function, based on Pressable state
 * const PressMe = ({ tokens, variant, children }) => {
 *   const getTokens = useThemeTokensCallback('PressMe', tokens, variant)
 *   const getPressableStyle = ({ pressed }) => {
 *     const { color, width, height } = getTokens({ pressed })
 *     return { backgroundColor: color, width, height }
 *   }
 *   return <Pressable style={getPressableStyle}>{children}</Pressable>
 * }
 *
 * @example
 * // Setting the theme in a parent and resolving it in a child based on child's state
 * const MenuButton = ({ tokens, variant, ...buttonProps }) => {
 *   // Define what theme, variant etc we want in this component...
 *   const getTokens = useThemeTokensCallback('Button', tokens, variant)
 *   // ...resolve them in another component based on its state (e.g. press, hover...)
 *   return <ButtonBase tokens={getTokens} accessibilityRole="menuitem" {...buttonProps} />
 * }
 *
 * @example
 * // Chaining multiple themes together, sharing the same variants and state
 * const GlowingLink = ({ tokens, variant, children }) => {
 *   // applies a small theme for a glowing effect, using same variants and states as "link"
 *   const getGlowTokens = useThemeCallback('GlowingLink', tokens, variant)
 *   // applies link state to get "Link" theme tokens and merge "GlowingLink" tokens on top
 *   const getTokens = useThemeCallback('Link', getGlowTokens, variant)
 *   // renders a link with both themes applied, both based on current link state
 *   return <LinkBase tokens={getTokens}>{children}</LinkBase>
 * }
 *
 * @param {string} componentName - the name as defined in the theme schema of the component whose theme is to be used
 * @param {TokensProp} [tokens] - every themed component should accept a `tokens` prop allowing theme tokens to be overridden
 * @param {AppearanceSet} [variants] - variants passed in as props that don't change dynamically
 * @returns {(states: AppearanceSet, tokenOverrides?: TokensProp) => TokensSet}
 *    - callback function that returning an overridable tokens set for current state. Only pass
 *      tokenOverrides in rare cases where tokens overrides are also generated outside hook scope,
 *      e.g. if one theme tokens callback needs to pass certain token overrides to another.
 */
export const useThemeTokensCallback = function (componentName) {
  let tokens = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let variants = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  const theme = useTheme();
  const componentTheme = getComponentTheme(theme, componentName);
  const getThemeTokensCallback = useCallback((states, tokenOverrides) => {
    const appearances = mergeAppearances(variants, states);
    const resolvedTokens = resolveThemeTokens(tokens, appearances, tokenOverrides);
    return getThemeTokens(componentTheme, resolvedTokens, appearances);
  }, [componentTheme, tokens, variants]);
  return getThemeTokensCallback;
};